import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import { isGroupShape } from '@/lib/groups/group-kind'
import { FROZEN_UNGROUP_MESSAGE, LOCKED_UNGROUP_MESSAGE } from '@/lib/editor/freeze-object-messages'

function isShapeSelectable(shape: ShapeData, editScopeGroupId?: string | null): boolean {
  if (shape.hidden === true) return false
  if (editScopeGroupId) return shape.parentGroupId === editScopeGroupId
  return !shape.parentGroupId
}

/**
 * True when every entry in `selectedRoots` is an unlocked group that is selectable in the
 * current edit scope. Callers should pass ids already normalized via
 * `normalizeSelectableIdsForScope` (same as shape-manager).
 */
export function selectedRootsAllowUngroup(
  selectedRoots: readonly string[],
  shapes: readonly ShapeData[],
  editScopeGroupId?: string | null
): boolean {
  if (selectedRoots.length === 0) return false

  const scope = editScopeGroupId ?? null

  for (const id of selectedRoots) {
    const shape = shapes.find((s) => s.id === id)

    if (
      !shape ||
      !isGroupShape(shape) ||
      !isShapeSelectable(shape, scope) ||
      shape.locked === true ||
      shape.frozen === true
    ) {
      return false
    }
  }

  return true
}

/** True when any selected root is a group shape (ungroup may apply). */
export function selectionIncludesGroupShape(
  selectedRoots: readonly string[],
  shapes: readonly ShapeData[]
): boolean {
  for (const id of selectedRoots) {
    const shape = shapes.find((candidate) => candidate.id === id)

    if (shape && isGroupShape(shape)) {
      return true
    }
  }

  return false
}

/**
 * The toast message explaining why the selection can't be ungrouped, or `null` when no selected
 * group is frozen or locked. Frozen wins over locked when the selection has both. Pass ids already
 * narrowed to the edit scope (as `canUngroupSelectedObjects` does), so the message always describes
 * a group that Ungroup would actually act on.
 */
export function ungroupBlockedMessage(
  selectedRoots: readonly string[],
  shapes: readonly ShapeData[]
): string | null {
  let hasLockedGroup = false

  for (const id of selectedRoots) {
    const shape = shapes.find((candidate) => candidate.id === id)

    if (!shape || !isGroupShape(shape)) continue
    if (shape.frozen === true) return FROZEN_UNGROUP_MESSAGE
    if (shape.locked === true) hasLockedGroup = true
  }

  return hasLockedGroup ? LOCKED_UNGROUP_MESSAGE : null
}

type UngroupSelectionTarget = Pick<
  ShapeManagerRef,
  'canUngroupSelectedObjects' | 'getUngroupBlockedMessage' | 'ungroupSelectedObjects'
>

/**
 * Ungroup the current selection, or report why it can't be. The shortcut, the menu bar and the
 * context menu all go through this so they behave the same. `notifyBlocked` is the toast, passed in
 * because `lib/` holds no UI. Returns true when an ungroup ran.
 */
export function tryUngroupSelection(
  mgr: UngroupSelectionTarget,
  notifyBlocked: (message: string) => void
): boolean {
  const blockedMessage = mgr.getUngroupBlockedMessage()

  if (blockedMessage) {
    notifyBlocked(blockedMessage)
    return false
  }

  if (!mgr.canUngroupSelectedObjects()) return false
  mgr.ungroupSelectedObjects()

  return true
}

/**
 * True when Ungroup should be greyed out. A frozen or locked group stays clickable so the click can
 * explain why nothing happens.
 */
export function isUngroupUnavailable(
  mgr: Pick<ShapeManagerRef, 'canUngroupSelectedObjects' | 'getUngroupBlockedMessage'> | null
): boolean {
  if (!mgr) return true

  return !mgr.canUngroupSelectedObjects() && !mgr.getUngroupBlockedMessage()
}
